import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import { Mail, ArrowLeft } from 'lucide-react';
import PublicHeader from '../components/PublicHeader';

const ForgotPasswordPage = () => {
  const forgotPassword = useAuthStore((state) => state.forgotPassword);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    const result = await forgotPassword(email);

    if (result.success) {
      toast.success(result.message || 'Reset link sent to your email');
      setIsSubmitted(true);
      // For development: If a token is returned, show it in console
      if (result.token) {
        console.log('Dev Reset Token:', result.token);
        // You could even toast it in dev mode if you want, but for now just console.
      }
    } else {
      toast.error(result.error || 'Failed to request password reset');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex flex-col">
      <PublicHeader />
      <div className="flex flex-1 items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          {/* Page Title */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white">Reset Password</h1>
            <p className="text-gray-400 mt-2">Enter your email to receive a password reset link.</p>
          </div>

          {/* Form Container */}
          <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-8 shadow-2xl">
            {isSubmitted ? (
              <div className="text-center space-y-6">
                <div className="w-16 h-16 bg-blue-500/20 text-blue-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail size={32} />
                </div>
                <h3 className="text-xl font-semibold text-white">Check Your Email</h3>
                <p className="text-gray-300">
                  We've sent a password reset link to <strong>{email}</strong>. Please check your inbox and spam folder.
                </p>
                <div className="pt-4">
                  <Link 
                    to="/login"
                    className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    <ArrowLeft size={16} className="mr-2" />
                    Back to Login
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your registered email"
                      required
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/30"
                >
                  {loading ? 'Sending link...' : 'Send Reset Link'}
                </button>

                {/* Back to Login */}
                <div className="text-center pt-2">
                  <Link 
                    to="/login"
                    className="inline-flex items-center text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    <ArrowLeft size={16} className="mr-1" />
                    Back to Login
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
